import React, { useState, useEffect, useRef, Fragment } from "react"
import "./style.scss";
import { Question, QuestionData } from "../../global-interfaces";
import { format, ScaleLinear } from "d3";
import { groups, max } from "d3-array";
import Classnames from "classnames";
import { checkIfMobile, makeScaleLinear } from "../../utils";
import styleVars from "../../styling/variables.scss";

const pct_format: any = format(".0%");

interface Props {
  primary: Question;
  secondary?: Question;
  color: string | null;
  inline?: boolean;
}

export default function Horizontal({ primary, secondary, color, inline }: Props) {
  
  /* APP STATE */
  const data = primary.data
  const chartRef = useRef(null)
  const width = useChartSize()

  function useChartSize(): number | undefined {
    const [chartWidth, setChartWidth] = useState<number | undefined>(undefined);
    useEffect(() => {
      function handleResize() {
        if (chartRef.current) {
          // @ts-ignore
          setChartWidth(chartRef.current.getBoundingClientRect().width)
        }
      }
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    return chartWidth;
  }

  /* TRANSFORMATIONS */
  const nestedData = groups(data, d => d.label_group)
  /** custom scale so that if the values are all less than 25%, the domain is 0-50% instead of 0-100% */
  const noDots = (max(data, d => d.value) === +primary.scale)
  const scale = makeScaleLinear([0, primary.scale])
  /* This is a specific check for this chart width rather than the  entire width of the screen (i.e. check if mobile) */
  const columns = (nestedData.length > 1) && (width && width > +styleVars.mobileBreakpoint.slice(0, -2))

  /* RENDER JSX */
  return (
    <div className={Classnames("Horizontal", color, { inline, columns })} ref={chartRef}>
      {/* BREAKDOWNS (groupings, like "by age") */}
      {nestedData.map(( [breakdown, b_data] : any, i : number ) => (
        <div key={i} className={Classnames("label-group-parent", 
            breakdown.toLowerCase().replace(/([^a-zA-Z0-9])/g, "-")
          )}>
          {breakdown !== "" && <div className={"label-group-div"}>
              {!inline && <hr />}
              <span className={"label-group"}>{breakdown}</span>
          </div>}
          {/** COMPARISON (either an array of one values or two values that take on opacity). This includes several scenarios:
           * (1) there is one bar with one label. This is represented in the final `else return`. 
           * (2) there is a secondary questioned passed in, which creates the two-toned bars. 
           * (3) there is a secondary in which the labels are not the same. This creates two-toned bars, but also repeats the inline label for both bars. 
          */}
          {b_data && b_data.map((d: any, j: number) => {
            const secondaryData = secondary && secondary.data.find(e => e.data_id === d.data_id)
            if (secondaryData) {
              return <>
                {!inline && <DataNameLabel label={d.label}/>}
                <Bar { ...{ inline, noDots, width, scale, d }}/>
                {secondaryData.label !== d.label && <DataNameLabel label={secondaryData.label}/>}
                <Bar { ...{ inline, noDots, width, scale, d: secondaryData, secondary: true }}/>
              </>
            } else return <>
              {!inline && <DataNameLabel label={d.label}/>}
              <Bar { ...{ inline, noDots, width, scale, d }}/>
            </>
          })}
        </div>
      ))}
    </div> 
  )
}

interface BarProps {
  inline: boolean | undefined;
  noDots: boolean | undefined;
  width: number | undefined;
  scale: ScaleLinear<number, number>, 
  d: QuestionData, 
  secondary?: boolean;
}

function Bar({ inline, noDots, width, scale, d, secondary } : BarProps) {
  return <div key={d.id} className={Classnames("bar-group")}>
    {!noDots && <hr className="bar-dots" />}
    <div
      className={Classnames("bar-measure", {
        lighter: secondary,
        "outside-bar": !checkIfMobile() ? d.value < 0.07 : d.value < 0.1 // TODO make this more dynamic
      })}
      style={{ 
        width: pct_format(scale(d.value))
        // animationDelay: `${+d.data_id * 50}ms`
      }}
    >
      {inline && <div className={"label-data-name"}>{d.label}</div>}
    <div className={"label-data"}
        style={{ left: (d.value * (width || 0)) }}
      >{pct_format(d.value)}</div>
    </div>
  </div>
}

function DataNameLabel({ label }: { label: string }) {
  return <div className={Classnames("label-data-name")}>{label}</div>
}